/**
 * QMP-FE-8b — THE v83 FRAME (docs/design/mockups/mockup-qmp-v83-official.html,
 * owner approval 2026-10-29): the QMP's own rail of five steps, each with its
 * state, and Approvals as the rail's link; the header (PROJECT QUALITY,
 * "Quality Management Plan", the plan's state, the status line); the global
 * navigation folded into ☰ (`<Layout foldNav>`). The CONTENT of each step is
 * still the v79 one (QMP-FE-1 to QMP-FE-6) until its own QMP-FE-8 train.
 *
 * STEPS 2, 3 AND 4 have subareas, one at a time (v83 `PAGE_OF`: 2 = ITP
 * preparation, hold, witness; 3 = NCRs, RFIs, visibility, AI; 4 = handover,
 * punch list). Continue saves the one on screen ("Saves this section, then
 * continues") and moves to the next. The rules editor lives here, so an
 * unsaved subarea still shows on the rail while the person is on another
 * step. Step 5 (Review & publish) is the last: no Continue.
 *
 * QMP-FE-8d — steps 2, 3 and 4 open on their v83 LIST (`RuleListPage`): one
 * row per rule with its served title and meaning. A row's button opens TODAY's
 * editor of that subarea (the subareas, Continue and Back as above); Back from
 * the first subarea, or the step on the rail, returns to the list. On the list
 * the footer is v83's: "← Back · Draft saved … · Continue →", and Continue
 * saves nothing (nothing is being edited there).
 *
 * QMP-FE-8e1 — the rows of STEP 2 (ITP preparation and endorsement, hold
 * points, witness points) open v83's FULL-SCREEN editor (`RuleEditorPage`)
 * instead: one rule, the common rule or one QWP's, with its own footer
 * (Cancel / Follow the common rule again / Save in draft); the page footer is
 * not drawn under it. While it holds unsaved changes the rail and Approvals
 * are refused with v83's sentence. The rows of steps 3 and 4, and the links
 * of Review & publish that name a subarea, still open TODAY's editor.
 *
 * QMP-FE-8e2 — the rows of STEP 3 (NCRs, RFIs, visibility, AI) open the same
 * full-screen editor. Step 4's rows and the Review & publish links are as the
 * 8e1 left them (today's editor).
 *
 * QMP-FE-8c — STEP 1 is v83's `packagePage`, a view (`PackagesPartiesStep`):
 * «Edit» on a row, or «+ Add a Quality Work Package», opens the full-screen
 * editor «<QWP> · parties» (`PackagePartiesPage`), which saves the three
 * positions in ONE act (D43, `PUT /qwps/{id}/structure`). Like the rule
 * editor it has its own footer, and while it holds unsaved changes the rail
 * and Approvals are refused with v83's sentence (Cancel always leaves).
 *
 * v83 has no crumb of its own, no opening sentence and no "About this setup":
 * they left with this frame. No localStorage.
 */
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import PackagesPartiesStep from '../components/qmp/PackagesPartiesStep';
import PackagePartiesPage from '../components/qmp/PackagePartiesPage';
import ItpRulesStep, { SUBS2, LoadError, Subnav } from '../components/qmp/ItpRulesStep';
import HandoverStep, { SUBS3 } from '../components/qmp/HandoverStep';
import NcrRfiStep, { SUBS4 } from '../components/qmp/NcrRfiStep';
import AiUsageStep, { SUBS5 } from '../components/qmp/AiUsageStep';
import PublishStep, { companyNames } from '../components/qmp/PublishStep';
import ApprovalsStep from '../components/qmp/ApprovalsStep';
import RuleListPage from '../components/qmp/RuleListPage';
import RuleEditorPage, {
  FULL_EDITOR_SECTIONS, STEP3_EDITOR_SECTIONS, UNSAVED_LEAVE,
} from '../components/qmp/RuleEditorPage';
import useProjectQmp from '../hooks/useProjectQmp';
import useQmpRuleEditor from '../hooks/useQmpRuleEditor';
import { formatDay } from '../lib/qmpApprovals';
import { footerNote } from '../lib/qmpRuleRows';

// QMP-FE-8b — mockup v83 `steps` (docs/design/mockups/mockup-qmp-v83-official.html
// l. 1535), verbatim. Approvals is the rail's own link, not a step (l. 1493).
export const STEPS = [
  'Quality Work Packages & Parties', 'Inspections & sign-offs', 'NCRs, RFIs & permissions',
  'Handover & acceptance', 'Review & publish',
];
export const APPROVALS = 'approvals';

// QMP-FE-8b — the subareas of each v83 page (`PAGE_OF`, l. 1534): step 2 =
// ITP preparation, hold points, witness points; step 3 = NCRs, RFIs,
// visibility, AI; step 4 = handover, punch list. Their titles are the
// server's (`GET /qmp` `standard_steps`, QMP-FE-9).
const SUBS = {
  2: SUBS2, 3: SUBS4.concat(SUBS5), 4: SUBS3,
};
/** The v83 page a subarea lives on (blockers and links name the subarea). */
export const PAGE_OF_SECTION = {};
Object.entries(SUBS).forEach(([k, list]) => { list.forEach((s) => { PAGE_OF_SECTION[s] = Number(k); }); });

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Steps 2-5 draw their selects from the served choices, which come with the
 *  plan (QMP-FE-9): until it has served them, the page's loading state (never
 *  an empty select); if the plan could not be read, the plan's own refusal. */
function RulesLoading({ editor }) {
  if (editor.loadError) return <LoadError editor={editor} />;
  return (
    <div
      className="bg-[#0d1526] border border-[#16223a] rounded-[12px] px-[26px] py-[24px] text-[12.5px] text-[#94a3b8]"
      data-testid="qs-rules-loading"
    >
      Loading…
    </div>
  );
}

// QMP-FE-8b — mockup v83 `.cq-side` (l. 1260-1269, 1329-1330): the QMP's own
// rail, dark values of its tokens. Each step carries its state (`readiness`,
// l. 1700-1707) — read from what the server serves, never stored here.
const RAIL_STEP = 'flex items-start gap-[9px] text-left w-full border rounded-[8px] px-[9px] py-[12px] '
  + 'min-h-[44px] my-[3px] text-[#f1f5fb]';
const RAIL_ON = 'bg-[#32251e] border-[#ff8b32]';
const RAIL_OFF = 'bg-transparent border-transparent';

/** The state line of each step (v83 `readiness`, draft labels). */
export function railStates(editor, qmp) {
  const status = editor.status || {};
  const reviewOf = (sections) => sections.reduce(
    (n, s) => n + Object.keys((status[s] && status[s].review) || {}).length, 0,
  );
  const dirtyOf = (sections) => sections.some((s) => status[s] && status[s].dirty);
  const savedOf = (sections) => sections.every((s) => status[s] && status[s].saved);
  const out = { 1: 'Saved · active now' };
  [2, 3, 4].forEach((k) => {
    const n = reviewOf(SUBS[k]);
    if (n) out[k] = { text: `${n} to review`, warn: true };
    else if (dirtyOf(SUBS[k])) out[k] = 'Unsaved changes';
    else if (savedOf(SUBS[k])) out[k] = 'Saved in draft';
    else out[k] = 'Not saved yet';
  });
  const items = [2, 3, 4].reduce((n, k) => n + reviewOf(SUBS[k]), 0);
  const rev = Number((qmp.current_revision && qmp.current_revision.revision) || 0);
  const nothing = (qmp.publish_blockers || []).some((b) => b && b.code === 'NOTHING_CHANGED');
  out.items = items;
  if (items) out[5] = { text: `${plural(items, 'item', 'items')} to review`, warn: true };
  else if (rev && nothing) out[5] = 'No changes yet';
  else out[5] = 'Ready to review';
  return out;
}

function Rail({
  step, onGo, states, approvals,
}) {
  return (
    <aside
      className="px-[12px] py-[24px] bg-[#101a2b] border-r border-[#2b3c56] max-[800px]:p-[12px] max-[800px]:border-r-0 max-[800px]:border-b"
      data-testid="qs-rail"
    >
      <div className="text-[11px] font-bold tracking-[.08em] text-[#afbdd0] px-[8px] pb-[14px] max-[800px]:hidden">
        QUALITY MANAGEMENT PLAN
      </div>
      <nav
        aria-label="QMP steps"
        className="max-[800px]:grid max-[800px]:grid-cols-3 max-[800px]:gap-[4px] max-[580px]:grid-cols-2"
        data-testid="qs-stepper"
      >
        {STEPS.map((label, i) => {
          const k = i + 1;
          const on = step === k;
          const st = states[k];
          const warn = Boolean(st && st.warn);
          return (
            <button
              key={label}
              type="button"
              onClick={() => onGo(k)}
              data-testid={`qs-step-${k}`}
              aria-current={on ? 'step' : undefined}
              className={`${RAIL_STEP} ${on ? RAIL_ON : RAIL_OFF}`}
            >
              <span
                className="grid place-items-center border border-[#2b3c56] rounded-full w-[23px] h-[23px] flex-[0_0_23px] text-[12px]"
                data-testid={`qs-step-mark-${k}`}
              >
                {k}
              </span>
              <span>
                <span className="block text-[13px] font-bold">{label}</span>
                <span
                  className={`block text-[11px] mt-[3px] ${warn ? 'text-[#ffa9b2]' : 'text-[#afbdd0]'}`}
                  data-testid={`qs-step-state-${k}`}
                >
                  {st && typeof st === 'object' ? st.text : st}
                </span>
              </span>
            </button>
          );
        })}
      </nav>
      <div className="border-t border-[#2b3c56] mx-[9px] my-[18px] max-[800px]:hidden" />
      <button
        type="button"
        onClick={() => onGo(APPROVALS)}
        aria-current={step === APPROVALS ? 'page' : 'false'}
        className={`${RAIL_STEP} ${step === APPROVALS ? RAIL_ON : RAIL_OFF} max-[800px]:w-auto max-[800px]:mt-[7px]`}
        data-testid="qs-step-approvals"
      >
        <span className="text-[13px]">
          Approvals
          {approvals && <span data-testid="qs-approvals-count">{` ${approvals}`}</span>}
        </span>
      </button>
      <div className="text-[12px] text-[#afbdd0] px-[9px] pt-[24px] leading-[1.8] max-[800px]:hidden" data-testid="qs-rail-note">
        The QMP sets the rules.
        <br />
        Quality Work Packages organise the work.
        <br />
        Handover delivers it.
      </div>
    </aside>
  );
}

// QMP-FE-10 — the two addresses the QMP had before (the v8 plan's and this
// wizard's own while the two lived side by side) land here, on the project
// they named.
export function QualitySetupRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}/quality-setup`} replace />;
}

export default function QualitySetupPage() {
  const { projectId } = useParams();
  const qmp = useProjectQmp(projectId);
  const editor = useQmpRuleEditor(projectId, qmp);
  const [step, setStep] = React.useState(1);
  const [subs, setSubs] = React.useState({
    2: SUBS[2][0], 3: SUBS[3][0], 4: SUBS[4][0],
  });
  const setSub = (k, s) => setSubs((prev) => ({ ...prev, [k]: s }));
  // QMP-FE-8d — false = the step's list; true = today's editor of `subs[step]`.
  // QMP-FE-8e1 — 'full' = the v83 full-screen editor of `subs[step]`.
  const [editing, setEditing] = React.useState(false);
  const [fullDirty, setFullDirty] = React.useState(false);
  // QMP-FE-8c — null = step 1's table; `{ id }` = the parties editor of that
  // package (`id` null = a new one).
  const [parties, setParties] = React.useState(null);
  const [feedback, setFeedback] = React.useState(null);
  const full = editing === 'full';
  const partiesOpen = step === 1 && parties !== null;
  const fullScreen = full || partiesOpen;
  const say = React.useCallback((text, error) => {
    setFeedback(text ? { text, error: Boolean(error) } : null);
  }, []);

  const go = (k) => {
    if (fullScreen && fullDirty) { say(UNSAVED_LEAVE, true); return; }
    setFeedback(null);
    setStep(k);
    setEditing(false);
    setParties(null);
    toTop();
  };
  const openEditor = (s) => {
    setFeedback(null);
    setSub(step, s);
    const fullScreen = FULL_EDITOR_SECTIONS.includes(s) || STEP3_EDITOR_SECTIONS.includes(s);
    setEditing(fullScreen ? 'full' : true);
    toTop();
  };
  const openParties = (id) => {
    setFeedback(null);
    setParties({ id: id || null });
    toTop();
  };
  const closeFull = React.useCallback((message) => {
    setEditing(false);
    setParties(null);
    setFullDirty(false);
    setFeedback(message ? { text: message, error: false } : null);
    toTop();
  }, []);
  // A link that names a subarea (a blocker, "Review") goes to the v83 page
  // that holds it; `k` is kept only for a page with no subareas.
  const goTo = (k, s) => {
    if (fullScreen && fullDirty) { say(UNSAVED_LEAVE, true); return; }
    const page = (s && PAGE_OF_SECTION[s]) || k;
    go(page);
    if (s && SUBS[page]) { setSub(page, s); setEditing(true); }
  };

  function toTop() {
    if (typeof window !== 'undefined' && window.scrollTo) {
      try { window.scrollTo({ top: 0 }); } catch (e) { /* jsdom */ }
    }
  }

  // Continue saves the subarea on screen before moving on (unchanged).
  const listPage = Boolean(SUBS[step]) && !editing;
  const list = listPage || full ? null : SUBS[step];
  const cur = list ? subs[step] : null;
  const subIndex = list ? list.indexOf(cur) : -1;
  const onContinue = async () => {
    if (list) {
      if (editor.needsSave(cur)) {
        const ok = await editor.save(cur);
        if (!ok) return;
      }
      if (subIndex < list.length - 1) { setSub(step, list[subIndex + 1]); toTop(); return; }
    }
    go(step + 1);
  };
  const onBack = () => {
    if (step === APPROVALS) { go(STEPS.length); return; }
    if (list && subIndex > 0) { setSub(step, list[subIndex - 1]); toTop(); return; }
    if (list) { setEditing(false); toTop(); return; }
    go(step - 1);
  };
  const last = step === APPROVALS || step >= STEPS.length;
  let nextLabel = last ? '' : `Continue to ${STEPS[step]}`;
  let backLabel = '← Back';
  const subTitle = (s) => editor.titles[s];
  if (list && list.length > 1) {
    if (subIndex < list.length - 1 && subTitle(list[subIndex + 1])) {
      nextLabel = `Next: ${subTitle(list[subIndex + 1])}`;
    }
    if (subIndex > 0 && subTitle(list[subIndex - 1])) backLabel = `← ${subTitle(list[subIndex - 1])}`;
  }
  if (listPage && !last) nextLabel = 'Continue →';
  const rulesWait = Boolean(SUBS[step]) && !editor.ready;
  const wide = !listPage && !full && (step === 2 || step === 4);

  // The header and the rail read the plan as served (v83 `render`, l. 2142-2163).
  const states = railStates(editor, qmp);
  const rev = Number((qmp.current_revision && qmp.current_revision.revision) || 0);
  const nothing = (qmp.publish_blockers || []).some((b) => b && b.code === 'NOTHING_CHANGED');
  const inForce = Boolean(rev && nothing);
  const summary = qmp.approvals_summary || {};
  const { items } = states;
  const dirty = Object.values(editor.status || {}).some((x) => x && x.dirty) || (fullScreen && fullDirty);
  const names = companyNames(editor);
  const cr = qmp.current_revision || {};
  const badge = inForce
    ? { text: `Revision ${rev} · published`, good: true }
    : { text: `Draft · future Revision ${rev + 1}${rev ? ` · Revision ${rev} in force` : ''}`, good: false };
  let saveState = dirty ? 'Unsaved changes on this screen' : 'Draft saved · rules not published yet';
  if (inForce) {
    const who = [cr.declared_by_name, names[cr.declared_by_participation_id]].filter(Boolean).join(' · ');
    const when = formatDay(cr.declared_at);
    saveState = `Published${who ? ` by ${who}` : ''}${when ? ` · ${when}` : ''}`;
  }
  let readiness = items ? `${plural(items, 'item', 'items')} to review before publishing` : 'Ready to review and publish';
  if (inForce) readiness = `${Number(summary.packages_approved || 0)} of ${Number(summary.packages_total || 0)} QWPs with agreed rules`;
  const readOnly = inForce || !editor.canGovern;
  const pageDirty = Boolean(SUBS[step]) && SUBS[step].some((s) => editor.status[s] && editor.status[s].dirty);
  const listNote = footerNote({ inForce, revision: rev, dirty: pageDirty });
  const approvalsCount = summary.revision
    ? `${Number(summary.packages_approved || 0)}/${Number(summary.packages_total || 0)} QWPs` : '';

  return (
    <Layout foldNav>
      <div
        className="min-h-full bg-[#0a111d] text-[#f1f5fb] text-[14px] leading-[1.5] antialiased grid grid-cols-[202px_minmax(0,1fr)] max-[800px]:grid-cols-1"
        style={{ fontFamily: 'Manrope,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif' }}
        data-testid="quality-setup-page"
      >
        <Rail step={step} onGo={go} states={states} approvals={approvalsCount} />
        <main className="p-[25px] min-w-0 max-[800px]:p-[20px] max-[580px]:p-[16px]" data-testid="qs-main">
          <div className="flex items-center justify-between gap-[16px] flex-wrap">
            <div>
              <div className="text-[11px] tracking-[.1em] text-[#afbdd0] font-bold">PROJECT QUALITY</div>
              <h1 className="text-[23px] leading-[1.2] mt-[4px] tracking-[-.5px] font-medium max-[580px]:text-[21px]" data-testid="qs-title">
                Quality Management Plan
              </h1>
            </div>
            <span
              className={`inline-block px-[9px] py-[4px] rounded-[5px] text-[12px] font-bold ${
                badge.good ? 'text-[#78d4ae] bg-[#122d28]' : 'bg-[#162238]'
              }`}
              data-testid="qs-plan-state"
            >
              {badge.text}
            </span>
          </div>
          <div
            className="flex flex-wrap gap-x-[20px] gap-y-[8px] justify-between mt-[14px] mb-[24px] text-[12px] text-[#afbdd0] pb-[14px] border-b border-[#2b3c56]"
            data-testid="qs-status-line"
          >
            <span data-testid="qs-save-state">{saveState}</span>
            <span data-testid="qs-readiness">{readiness}</span>
          </div>

          <div className={wide ? 'max-w-[1600px]' : 'max-w-[1010px]'}>
            {feedback && (
              <div
                className={`px-[12px] py-[10px] rounded-[6px] mb-[14px] text-[13px] ${
                  feedback.error ? 'bg-[#332819] text-[#ffa9b2]' : 'bg-[#122d28] text-[#78d4ae]'
                }`}
                role={feedback.error ? 'alert' : 'status'}
                data-testid="qs-feedback"
              >
                {feedback.text}
              </div>
            )}
            {rulesWait && <RulesLoading editor={editor} />}
            {!rulesWait && listPage && (
              <RuleListPage
                page={step}
                editor={editor}
                served={qmp.standard_steps}
                readOnly={readOnly}
                onEdit={openEditor}
                projectId={projectId}
                revision={rev}
              />
            )}
            {!rulesWait && full && (
              <RuleEditorPage
                key={subs[step]}
                section={subs[step]}
                editor={editor}
                readOnly={readOnly}
                inForce={inForce}
                revision={rev}
                onClose={closeFull}
                onFeedback={say}
                onDirty={setFullDirty}
              />
            )}
            {!rulesWait && !listPage && !full && step === 2 && <ItpRulesStep editor={editor} sub={subs[2]} onSub={(s) => setSub(2, s)} />}
            {!rulesWait && !listPage && !full && step === 3 && SUBS4.includes(subs[3]) && (
              <NcrRfiStep editor={editor} sub={subs[3]} onSub={(s) => setSub(3, s)} subs={SUBS[3]} />
            )}
            {!rulesWait && !listPage && !full && step === 3 && !SUBS4.includes(subs[3]) && (
              <>
                <Subnav sub={subs[3]} onSub={(s) => setSub(3, s)} status={editor.status} subs={SUBS[3]} titles={editor.titles} />
                <AiUsageStep editor={editor} />
              </>
            )}
            {!rulesWait && !listPage && step === 4 && <HandoverStep editor={editor} sub={subs[4]} onSub={(s) => setSub(4, s)} />}
            {step === 5 && (
              <PublishStep editor={editor} qmp={qmp} projectId={projectId} onGo={goTo} />
            )}
            {step === APPROVALS && (
              <ApprovalsStep editor={editor} qmp={qmp} projectId={projectId} onGo={goTo} />
            )}
            {step === 1 && !partiesOpen && (
              <PackagesPartiesStep
                qmp={qmp}
                readOnly={readOnly}
                inForce={inForce}
                onEdit={openParties}
                onAdd={() => openParties(null)}
              />
            )}
            {partiesOpen && (
              <PackagePartiesPage
                key={parties.id || 'new'}
                projectId={projectId}
                qmp={qmp}
                qwpId={parties.id}
                companies={editor.companies}
                readOnly={readOnly}
                onClose={closeFull}
                onDirty={setFullDirty}
              />
            )}

            {!fullScreen && (
            <div className={`flex items-center mt-[22px] ${wide ? 'min-[1400px]:max-w-[calc(100%-302px)]' : ''}`}>
              <button
                type="button"
                onClick={onBack}
                className={`px-[18px] py-[10px] rounded-[9px] font-bold text-[13.5px] border border-[#1c2941] text-[#94a3b8] ${
                  step === 1 ? 'invisible' : ''
                }`}
                data-testid="qs-back"
              >
                {backLabel}
              </button>
              {Boolean(list) && editor.canGovern && (
                <span className="ml-[14px] text-[11.5px] text-[#5b6b85]" data-testid="qs-savehint">
                  Saves this section, then continues
                </span>
              )}
              {listPage && (
                <span className="flex-1 mx-[14px] text-[12px] text-[#afbdd0]" data-testid="qs-list-note">
                  {listNote}
                </span>
              )}
              {!last && (
                <button
                  type="button"
                  onClick={onContinue}
                  disabled={Boolean(editor.saving) || rulesWait}
                  className="ml-auto px-[18px] py-[10px] rounded-[9px] font-bold text-[13.5px] bg-[#f97316] text-[#180b02] disabled:opacity-45 disabled:cursor-not-allowed"
                  data-testid="qs-continue"
                >
                  {nextLabel}
                </button>
              )}
            </div>
            )}
          </div>
        </main>
      </div>
    </Layout>
  );
}
